import { memo } from 'react';
import Image from 'next/image';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { Box, Icon, List } from '@zillow/constellation-10';
import { IconNoteFilled, IconSparkleFilled, IconWarningFilled } from '@zillow/constellation-icons';

import { getPhotoUrls } from '@zg-rentals/zillow-rental-manager/utils';

import {
  backgroundImageContainerStyles,
  backgroundLoadingImageStyles,
  digitallyAlteredBadgeStyles,
  iconContainerStyles,
  overlayRecipe,
} from './styles';

import type { PhotoProps } from '@zg-rentals/maintenance-utils';

const ICON_TITLE_PHOTO_ERROR_MAP: Record<string, string> = {
  dmca: 'DMCA photo error',
  inappropriate: 'Inappropriate photo error',
};

const getUrlSrc = (url: string, errorDto: PhotoProps['errorDto'], isRunningInProd: boolean) => {
  if (!errorDto?.errorCode) {
    return url;
  }

  if (errorDto?.replacementPhotoId) {
    return getPhotoUrls(isRunningInProd, errorDto?.replacementPhotoId).thumb;
  }

  return '';
};

type Error = {
  photoId: string;
};

type Props = {
  photo: Partial<PhotoProps>;
  index: number;
  handlePhotoClick?: (arg: Partial<PhotoProps>) => void;
  errors?: Array<Error>;
  isUploading?: boolean;
  isDisabled?: boolean;
  /** Blocks sorting only; the photo button stays enabled so it keeps keyboard focus. */
  isSortDisabled?: boolean;
  isRunningInProd?: boolean;
  isDigitallyAlteredBadgeEnabled?: boolean;
};

// eslint-disable-next-line react/display-name
const SortableItem = memo(
  ({
    handlePhotoClick,
    photo,
    errors,
    isUploading,
    index,
    isDisabled = false,
    isSortDisabled = false,
    isRunningInProd = false,
    isDigitallyAlteredBadgeEnabled = false,
  }: Props) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
      id: photo.id || '',
      disabled: isDisabled || isSortDisabled,
    });

    if (isUploading) {
      return (
        <Box css={{ gridColumnEnd: 'span 1', height: '100%' }}>
          <Box css={backgroundImageContainerStyles}>
            <Box css={backgroundLoadingImageStyles} data-loading="true" />
          </Box>
        </Box>
      );
    }

    const hasPhotoCaptionError = (errors || []).filter((error) => error.photoId === photo.id).length > 0 || photo.error;
    const showDigitallyAlteredBadge = isDigitallyAlteredBadgeEnabled && Boolean(photo.isDigitallyAltered);
    const photoNumber = handlePhotoClick ? `photo-${index + 1}` : `placeholder-photo-${index + 1}`;

    const renderIcon = () => {
      // NOTE: errorDto should take priority over isAppropriate
      if (photo?.errorDto?.errorCode) {
        return (
          <Icon
            size="md"
            css={{ color: 'icon.feedback.notify.hero', zIndex: '5' }}
            aria-hidden="false"
            aria-describedby={`icon-warning-desc-${photoNumber}`}
            render={<IconWarningFilled />}
            title={ICON_TITLE_PHOTO_ERROR_MAP[photo?.errorDto?.errorCode] || 'Photo error'}
          >
            <desc id={`icon-warning-desc-${photoNumber}`}>
              {photo?.errorDto?.errorMessage
                ? photo?.errorDto?.errorMessage
                : 'Button to view inappropriate or DMCA photo error'}
            </desc>
          </Icon>
        );
      }

      // isAppropriate is deprecated and left in as fallback
      if (photo.isAppropriate === false) {
        return (
          <Icon
            size="md"
            css={{ color: 'icon.feedback.notify.hero', zIndex: '5' }}
            aria-hidden="false"
            aria-describedby={`icon-warning-desc-${photoNumber}`}
            render={<IconWarningFilled />}
            title="Inappropriate Photo Error"
          >
            <desc id={`icon-warning-desc-${photoNumber}`}>Button to view inappropriate photo error</desc>
          </Icon>
        );
      }

      if (hasPhotoCaptionError) {
        return (
          <Icon
            size="md"
            css={{ color: 'icon.feedback.notify.hero', zIndex: '5' }}
            aria-hidden="false"
            aria-describedby={`icon-warning-desc-${photoNumber}`}
            render={<IconWarningFilled />}
            title="Caption Error"
          >
            <desc id={`icon-warning-desc-${photoNumber}`}>Button to view or fix caption error</desc>
          </Icon>
        );
      }

      if (photo.caption) {
        return (
          <Icon
            size="md"
            css={{ color: 'icon.onImpact.neutral-fixed', zIndex: '5' }}
            aria-hidden="false"
            aria-describedby={`icon-caption-desc-${photoNumber}`}
            render={<IconNoteFilled />}
            title="Caption"
          >
            <desc id={`icon-caption-desc-${photoNumber}`}>Button to view or edit caption</desc>
          </Icon>
        );
      }

      return null;
    };

    const imageSrc = getUrlSrc(photo?.thumbUrl || photo?.url || '', photo?.errorDto, isRunningInProd);
    const icon = renderIcon();

    return (
      <List.Item
        css={{ opacity: isDisabled ? 'disabled' : 'action.active', paddingBlock: 0 }}
        aria-label={`Edit ${photoNumber}${showDigitallyAlteredBadge ? '; Digitally altered' : ''}${photo.caption ? `; hasCaption: ${photo.caption}` : ''}`}
      >
        <button
          disabled={isDisabled}
          type="button"
          ref={setNodeRef}
          style={{
            transform: CSS.Transform.toString(transform),
            transition,
            padding: 0,
            borderRadius: '4px',
            width: '100%',
            height: '100%',
          }}
          {...attributes}
          {...listeners}
          onClick={() => handlePhotoClick?.({ ...photo })}
        >
          <Box css={{ gridColumnEnd: 'span 1', height: '100%' }}>
            <Box css={backgroundImageContainerStyles}>
              <Box css={backgroundLoadingImageStyles} data-loading="true" />
              {imageSrc && (
                <Image
                  src={imageSrc}
                  alt={photo.caption || `Property photo ${index + 1}`}
                  fill
                  sizes="120px"
                  style={{ objectFit: 'cover' }}
                />
              )}
              <Box css={overlayRecipe.raw({ hasError: Boolean(hasPhotoCaptionError), isDisabled })} />
              {showDigitallyAlteredBadge && (
                <Box css={digitallyAlteredBadgeStyles}>
                  <Icon
                    size="sm"
                    aria-hidden="false"
                    aria-describedby={`icon-digitally-altered-desc-${photoNumber}`}
                    render={<IconSparkleFilled />}
                    title="Digitally altered"
                  >
                    <desc id={`icon-digitally-altered-desc-${photoNumber}`}>Digitally altered</desc>
                  </Icon>
                </Box>
              )}
              {icon && <Box css={iconContainerStyles}>{icon}</Box>}
            </Box>
          </Box>
        </button>
      </List.Item>
    );
  },
);

export default SortableItem;
